<template>
  <div class="mobile-calendar">
    <MobileHeader class="mobile-calendar__header" @close="close" />
    <MobileDataDisplay class="mobile-calendar__data" :selectionMode="selectionMode" :selected="selected" />
    <WeekDays class="mobile-calendar__week-days" />
    <div class="mobile-calendar__months">
      <div class="mobile-calendar__month" v-for="month in months" :key="month.format()">
        <MonthName class="mobile-calendar__month-name" :value="month"/>
        <Month :date="month" :valid="valid" :selected="selected" @change="selectDay" />
      </div>
    </div>
    <div class="mobile-calendar__toolbar mobile-calendar-toolbar calendar-toolbar">
      <button role="button" class="mobile-calendar-toolbar__button" @click.prevent.stop="notifyDataChanged">
        <span class="caption">Wybierz te daty</span>
        <span class="custom-icon--arr-right-sharp icon" />
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'

import MobileDataDisplay from './calendar/MobileDataDisplay.vue'
import MobileHeader from './calendar/MobileHeader.vue'
import Month from './calendar/Month.vue'
import MonthName from './calendar/MonthName.vue'
import MonthNavigator from './calendar/MonthNavigator.vue'
import WeekDays from './calendar/WeekDays.vue'

import AutoCloseMixin from '@/components/AutoCloseMixin'
import BodyFreezerMixin from '@/components/BodyFreezerMixin'

@Component({
  mixins: [
    AutoCloseMixin,
    BodyFreezerMixin,
  ],
  props: {
    mode: {
      required: false,
      type: String, // Start, end
      default: () => 'start',
    },
    selectedDates: {
      required: true,
      type: Object, // Moment.range
    },
    validDates: {
      required: true,
      type: Object, // Moment.range
    },
  },
  components: {
    MonthNavigator,
    WeekDays,
    Month,
    MonthName,
    MobileDataDisplay,
    MobileHeader,
  },
})
export default class MobileCalendar extends Vue {
  showDebugger = false

  // This is the current selection mode
  selectionMode = this.mode
  // This defines what are the currently valid dates
  // it changes from start selection (where all dates are valid)
  // to selectes.start-validDates.end when selecting end date
  valid = moment.range(this.validDates.start, this.validDates.end)
  // This is the currently selected range
  selected = moment.range(moment(this.selectedDates.start), moment(this.selectedDates.end))
  // This defines what date is displayed currently
  display = this._getDisplayRangeForCurrentSelectionMode()

  created () {
    log.debug('MobileCalendar.created selected', this.selected.start.format('YYYY-MM-DD'), this.selected.end.format('YYYY-MM-DD'))
    log.debug('MobileCalendar.created valid', this.valid.start.format('YYYY-MM-DD'), this.valid.end.format('YYYY-MM-DD'))

    this.freezeBody()
  }

  destroyed () {
    this.unfreezBody()
  }

  get months () {
    const hiddenMonthIds = [ 10, 11, 0, 1, 2 ] //From november to march, grecos doesn't offer trips in those months
    const result = []
    let date = moment(this.validDates.start)
    const end = moment(this.validDates.end).endOf('month')
    while (date.isBefore(end)) {
      const isHiddenMonth = hiddenMonthIds.includes(moment(date).month())
      if (!isHiddenMonth) {
        result.push(moment(date))
      }
      date = date.add(1, 'month')
    }
    return result
  }

  _getDisplayRangeForStartMode () {
    log.debug('MobileCalendar._getDisplayRangeForStartMode', 1)
    return moment.range(
      moment(this.selected.start).startOf('month'),
      moment(this.selected.start).add(1, 'month').endOf('month'))
  }

  _getDisplayRangeForEndMode () {
    log.debug('MobileCalendar._getDisplayRangeForEndMode')
    if (!this.selected.end.isSame(moment(0)) && this.selected.end.diff(this.selected.start, 'month') > 1) {
      log.debug('_getDisplayRangeForEndMode', 1)
      return moment.range(
        moment(this.selected.end).subtract(1, 'month').startOf('month'),
        moment(this.selected.end).endOf('month'))
    } else {
      log.debug('_getDisplayRangeForEndMode', 2)
      return moment.range(
        moment(this.selected.start).startOf('month'),
        moment(this.selected.start).add(1, 'month').endOf('month'))
    }
  }

  _getDisplayRangeForSelectionMode (mode) {
    log.debug('MobileCalendar._getDisplayRangeForSelectionMode', mode)
    if (mode === 'start') {
      return this._getDisplayRangeForStartMode()
    } else {
      return this._getDisplayRangeForEndMode()
    }
  }

  _getDisplayRangeForCurrentSelectionMode () {
    log.debug('MobileCalendar._getDisplayRangeForCurrentSelectionMode', this.selectionMode)
    return this._getDisplayRangeForSelectionMode(this.selectionMode)
  }

  selectStart (day) {
    log.debug('MobileCalendar.selectStart', day.format('YYYY-MM-DD'))
    this.selected = moment.range(day, 0)
    this.valid = moment.range(moment(day).add(1, 'day'), this.validDates.end)
  }

  selectEnd (day) {
    log.debug('MobileCalendar.selectEnd', day.format('YYYY-MM-DD'))
    this.selected = moment.range(this.selected.start, day)
    this.valid = moment.range(this.validDates.start, this.validDates.end)
  }

  selectDay (day) {
    log.debug('MobileCalendar.selectDay', day.format('YYYY-MM-DD'))
    if (this.selectionMode === 'start') {
      this.selectStart(day)
      this._toggleSelectionModeInRangeMode()
    } else if (this.selectionMode === 'end') {
      this.selectEnd(day)
      this._toggleSelectionModeInRangeMode()
    }
  }

  _toggleSelectionModeInRangeMode () {
    log.debug('MobileCalendar._toggleSelectionModeInRangeMode')
    if (this.selectionMode === 'start') this.selectionMode = 'end'
    else if (this.selectionMode === 'end') this.selectionMode = 'start'
  }

  notifyDataChanged () {
    log.debug('MobileCalendar.notifyDataChanged')
    this.$emit('selectFrom', this.selected.start)
    this.$emit('selectTo', this.selected.end)
    this.close()
  }
}
</script>

<style lang="scss">
@import './consts';
@import './dialog';
@import './calendar/consts';

.mobile-calendar {
  background-color: white;

  display: flex;
  flex-direction: column;

  font-family: Gotham;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;

  &__header {
    flex: none;
    color: white;
    background-color: #0055AA;
    padding: 20px 20px 10px 20px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 54px;
  }

  &__data {
    color: white;
    background-color: #0055AA;
    padding: 5px 20px 10px 20px;
    position: fixed;
    top: 54px;
    left: 0;
    right: 0;
    z-index: calc(inherited + 3);
  }

  &__week-days {
    background-color: white;
    font-size: 16px;

    margin-top: 1px;
    margin-bottom: 12px;
    padding: 20px 20px 10px 20px;
    position: fixed;
    width: calc(100% - 40px);
    top: 108px;
    left: 20px;
    right: 50px;
  }

  &__month-name {
    margin-bottom: 12px;
  }

  &__months {
    margin-top: 155px;
    margin-bottom: 75px;
    overflow-y: auto;
    padding-right: -16px;
  }

  &__month {
    margin-bottom: 16px;
    padding-left: 20px;
    padding-right: 20px;
    font-size: 16px;

    & .month-name {
      font-size: 16px;
    }
  }

  &__toolbar {
    height: 75px;
    border-top: solid 1px white;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
  }

  & .month__week {
    height: 42px;
    font-size: 16px;
  }

  & .month table {
    width: 100%;
  }

  & .month__day {
    width: 36px;
    height: 42px;
  }

}

.mobile-calendar-toolbar {
  display: flex;
  justify-content: center;
  align-items: stretch;
  background-color: #eeeeee;
  height: 75px;

  &__button {
    flex: 1 1 auto;
    color: white;
    background-color: #009fea;
    border: none;
    outline: none;
    margin: 20px;

    & .caption {
      font-size: 12px;
      font-weight: bold;
      text-transform: uppercase;
    }
    & .icon {
      font-size: 10px;
      text-transform: uppercase;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/MobileCalendar.vue